<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_PENALTY')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="penaltyRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="penaltyList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <!-- Column: minimum amount  -->
      <template #cell(minimumAmount)="data">
        <p class="mb-0">
          {{ toAmount(data.value ) }}
        </p>
      </template>

      <!-- Column: maximum tranx -->
      <template #cell(maximumAmount)="data">
        <p class="mb-0">
          {{ toAmount(data.value ) }}
        </p>
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_PENALTY')"
              @click="onSelectPenaltyItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectPenaltyItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_PENALTY' : 'ACTIVATE_PENALTY')"
              @click="penaltyStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="penaltyRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="fee-name"
                v-model="penaltyFormData.name"
                :label="$t('Name')"
                :placeholder="$t('Enter name')"
                type="text"
                :error-name="$t('Name')"
                rules="required"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Name -->

            <!-- Charge Frequency -->
            <b-col md="6">
              <SelectField
                id="charge-frequency"
                v-model="penaltyFormData.frequency"
                :label="$t('Charge Frequency')"
                :placeholder="$t('Select')"
                :options="chargeFrequencyOptions"
                :error-name="$t('Charge Frequency')"
                rules="required"
                :reduce="option => option"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Charge Frequency -->
          </b-row>
          <b-row class="match-height">
            <!-- Charge Type -->
            <b-col md="6">
              <SelectField
                id="charge-type"
                v-model="penaltyFormData.chargeType"
                :label="$t('Charge Type')"
                :placeholder="$t('Select')"
                :options="chargeTypeOptions"
                :error-name="$t('Charge Type')"
                rules="required"
                :reduce="option => option.value"
                item="label"
                :readonly="isReadonly"
                :set-selected="setAmountValidation"
              />
            </b-col>
            <!-- Charge Type -->

            <!--  Penalty Value -->
            <b-col md="6">
              <InputField
                id="penalty-value"
                v-model.number="penaltyFormData.penaltyValue"
                :label="$t('Penalty Value')"
                :placeholder="placeholderLabel"
                type="number"
                :error-name="$t('Penalty Value')"
                :rules="isPercentageOrAmount"
                :readonly="isReadonly"
              />
            </b-col>
            <!--  Penalty Value -->
          </b-row>
          <!-- Percentage on Turnover (%) -->

          <ValidationObserver>
            <b-row>
              <!--  Maximum Charge Amount -->
              <b-col md="6">
                <InputField
                  id="maximum-fee"
                  v-model.number="penaltyFormData.maximumAmount"
                  :label="$t('Maximum Charge Amount')"
                  :placeholder="$t('Enter maximum charge amount')"
                  type="number"
                  vid="maxFee"
                  error-name="maxFee"
                  rules="required|positive"
                  :readonly="isReadonly"
                />
              </b-col>
              <!-- Maximum Charge Amount -->

              <!-- Minimum Charge Amount -->
              <b-col md="6">
                <InputField
                  id="minimum-Charge-amount"
                  v-model.number="penaltyFormData.minimumAmount"
                  :label="$t('Minimum Charge Amount')"
                  :placeholder="$t('Enter minimum charge amount')"
                  type="number"
                  :error-name="$t('Minimum Charge Amount')"
                  rules="required|amount:@maxFee|positive"
                  :readonly="isReadonly"
                />
              </b-col>
              <!-- Minimum Charge Amount  -->
            </b-row>

          </ValidationObserver>
          <!-- Fee Charge Cycle -->
          <b-row class="match-height">
            <b-col md="6">
              <SwitchButton
                v-model="penaltyFormData.applyPenaltyOnDueAndUnpaidPrincipal"
                :label="$t('Apply Penalty On Due & Unpaid Principal')"
                name="unpaid-principal"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SwitchButton
                v-model="penaltyFormData.applyPenaltyOnDueAndUnpaidInterest"
                :label="$t('Apply Penalty On Due & Unpaid Interest')"
                name="unpaid-interest"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SwitchButton
                v-model="penaltyFormData.applyPenaltyOnDueAndUnpaidPenalty"
                :label="$t('Apply Penalty On Due & Unpaid Penalty')"
                name="unpaid-penalty"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SwitchButton
                v-model="penaltyFormData.applyPenaltyOnDueAndUnpaidLoanFee"
                :label="$t('Apply Penalty On Due & Unpaid Loan Fee')"
                name="loan-fee"
                :readonly="isReadonly"
              />
            </b-col>

          </b-row>

        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchPenalties"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name')"
            :placeholder="$t('Enter name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Name -->

        <!-- Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Code')"
            :placeholder="$t('Enter code')"
            type="text"
          />
        </b-col>
        <!-- Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge,
  BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import {
  required, amount, positive,
} from '@validations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../forms/InputField.vue'
import SelectField from '../../forms/SelectField.vue'
import { toDateWithYear } from '../../../helpers/formatDate'
import {
  chargeFrequency, chargeType, computationModel, transactionType,
} from '../../../helpers/data'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import { accountPermissions } from '../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BSpinner,
    BModal,
    BForm,
    ValidationObserver,
    BDropdown,
    BDropdownItem,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
    SwitchButton,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      showSpinner: false,
      required,
      amount,
      positive,
      getStatusColor,
      modalTitle: this.$t('Create Penatly'),
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'code', sortable: true, label: 'Penalty Code' },
        { key: 'chargeType', sortable: true, label: 'Charge Type' },
        { key: 'penaltyValue', sortable: true, label: 'Penalty Value' },
        { key: 'frequency', sortable: true, label: 'Frequency' },
        { key: 'minimumAmount', sortable: true, label: 'Minimum Amount' },
        { key: 'maximumAmount', sortable: true, label: 'Maximum Amount' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      penaltyFormData: {
        name: '',
        chargeType: '',
        penaltyValue: 0,
        frequency: '',
        minimumAmount: 0,
        maximumAmount: 0,
        applyPenaltyOnDueAndUnpaidPrincipal: false,
        applyPenaltyOnDueAndUnpaidInterest: false,
        applyPenaltyOnDueAndUnpaidPenalty: false,
        applyPenaltyOnDueAndUnpaidLoanFee: false,
      },
      transactionTypeOptions: transactionType,
      feeComputationOptions: computationModel,
      // chargeTypeOptions: chargeType,
      // chargeFrequencyOptions: [
      //   { label: 'Daily', value: 'Daily' },
      //   { label: 'Weekly', value: 'Weekly' },
      //   { label: 'Monthly', value: 'Monthly' }
      // ],
      isPercentageOrAmount: 'required|positive',
      placeholderLabel: this.$t('Enter amount'),
      filter: {
        name: '',
        code: '',
        status: '',
        sortColumn: '',
      },
      penaltyList: [],
      toAmount,
    }
  },
  computed: {
    ...mapGetters({
      incomeGlOptions: 'account/incomeGlOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    chargeTypeOptions() {
      return chargeType.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    chargeFrequencyOptions() {
      return chargeFrequency.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchPenalties()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchPenalties()
    },
  },
  mounted() {
    this.fetchPenalties()
  },
  methods: {
    ...mapActions({
      getPenalties: 'account/getPenaltiesList',
      updatePenalty: 'account/updatePenaltyAction',
      createPenalty: 'account/createPenaltyAction',
      updatePenaltyStatus: 'account/updateStatus',
    }),
    setAmountValidation(selectedValue) {
      if (selectedValue === 'Percentage') {
        this.isPercentageOrAmount = 'required|max-value:100|positive'
        this.placeholderLabel = this.$t('Enter percentage')
      } else {
        this.isPercentageOrAmount = 'required|positive'
        this.placeholderLabel = this.$t('Enter amount')
      }
    },
    async penaltyAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createPenalty(this.penaltyFormData)
          : await this.updatePenalty(this.penaltyFormData)

        const { status } = await response
        if (status === 200) {
          this.fetchPenalties()
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const text = this.$t('Penalty was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${text}` })
        }
      } catch (error) {
        const requestToText = this.$t('Your request to')
        const reasonText = this.$t('Penalty failed. Reason')
        this.alertError({ text: `${requestToText} ${this.buttonLabel} ${reasonText}: ${error}` })
      } finally {
        this.isLoading = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.penaltyRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const penaltyText = this.$t('this Penalty?')
          this.confirm(this.penaltyAction, { text: `${areYouSure} ${this.buttonLabel} ${penaltyText}` })
        }
      })
    },
    onSelectPenaltyItem(items, activity, val) {
      try {
        this.penaltyFormData = { ...items }
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Penalty') : this.$t('Penalty')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async penaltyStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_PENALTY : accountPermissions.ACTIVATE_PENALTY

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updatePenaltyStatus({
            statusValues, actionType, routeName: 'Penalty', permission,
          })
          if (response.status === 200) {
            this.fetchPenalties()
            const requestToText = this.$t('Your request to')
            const text = this.$t('Penalty was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
            this.showSpinner = false
          }
        } catch (error) {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Penalty failed. Reason')
          this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const penaltyText = this.$t('this Penalty?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${penaltyText}` })
    },
    async fetchPenalties() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getPenalties({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_PENALTY,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.penaltyList = data.data
          this.totalCount = data.recordCount
        } else {
          this.penaltyList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    resetModal() {
      this.penaltyFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.modalTitle = this.$t('Create Penalty')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchPenalties()
    },
  },
}
</script>
